import { usePageLoading } from "@/contexts/page-loading.context";
import {
  authFormClass,
  authPageContainerClass,
} from "@/layout/auth-layout.styles";
import {
  Alert,
  ArrowBackIcon as ArrowBack,
  Button,
  Typography,
} from "@iluvatar/ui-kit";

import { optionalSearchString } from "@/utilities/search-params";
import {
  Link,
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import type { AuthError } from "firebase/auth";
import { useState } from "react";
import * as Yup from "yup";

import { FORM_GLOBAL_ERROR_KEY } from "@iluvatar/global/src/constants";
import { isErrorCode } from "@iluvatar/global/src/utilities/errors";

import { FormHelpers, RootFormProvider } from "../FormLib";
import { PasswordField } from "../components/Form/Fields/Input";
import { confirmPassword } from "../components/Form/Validation";
import { useToastContext } from "../contexts/toast.context";
// services && hooks
import { getAuthRuntime } from "../services/firebase-auth.runtime";

type PasswordResetFormValue = {
  password: string;
  passwordConfirmation: string;
};

const PASSWORD_RESET_VALIDATOR = Yup.object().shape({
  password: Yup.string().required(),
  passwordConfirmation: Yup.string()
    .required()
    .test(confirmPassword("password")),
});

const PasswordResetPage: React.FC = () => {
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const { addToast } = useToastContext();
  const [blocking, toggleBlocking] = useState(false);
  usePageLoading(blocking);

  const disabled = !search.oobCode;

  const resetPasswordFormSubmission = async (
    { password }: PasswordResetFormValue,
    helpers: FormHelpers<PasswordResetFormValue>,
  ) => {
    toggleBlocking(true);
    try {
      if (!search.oobCode || typeof search.oobCode !== "string") {
        helpers.setFieldError(
          FORM_GLOBAL_ERROR_KEY,
          "Password Reset code is missing or invalid",
        );
        toggleBlocking(false);
        return;
      }

      const { auth, confirmPasswordReset } = await getAuthRuntime();
      await confirmPasswordReset(auth, search.oobCode, password);

      addToast("Password has been reset");
      navigate({ to: "/login" });
    } catch (error) {
      // Error codes can be found here.
      // https://firebase.google.com/docs/reference/js/firebase.auth.Auth#sendpasswordresetemail
      const err = error as AuthError;
      if (
        isErrorCode(err, "auth/expired-action-code") ||
        isErrorCode(err, "auth/invalid-action-code") ||
        isErrorCode(err, "auth/invalid-verification-code") ||
        isErrorCode(err, "auth/code-expired")
      ) {
        helpers.setFieldError(
          FORM_GLOBAL_ERROR_KEY,
          "Password reset code expired or is invalid, try the email link again, or sent another reset email.",
        );
      } else {
        helpers.setFieldError(FORM_GLOBAL_ERROR_KEY, err.message);
      }
    }
    toggleBlocking(false);
  };

  return (
    <div className={`${authPageContainerClass} mx-auto`}>
      <Typography
        variant="h5"
        className={[
          "auth-title",
          "[color:rgb(var(--uik-subtle))] [margin-top:24px] [margin-bottom:24px]",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        Set New Password
      </Typography>
      {!search?.oobCode && (
        <Alert
          tone="danger"
          className="[width:100%] [margin-bottom:16px] [margin-top:16px]"
        >
          No reset Code provided
        </Alert>
      )}
      <RootFormProvider<PasswordResetFormValue>
        id="reset-password-form"
        initialValues={{
          password: "",
          passwordConfirmation: "",
        }}
        onSubmit={resetPasswordFormSubmission}
        validationSchema={PASSWORD_RESET_VALIDATOR}
        disabledEnterNextFieldNavigation
        className={`${authFormClass} gap-[16px]`}
      >
        <PasswordField
          required
          label="Password"
          name="password"
          disabled={disabled || blocking}
          className="auth-form-element"
        />
        <PasswordField
          required
          label="Confirm Password"
          name="passwordConfirmation"
          disabled={disabled || blocking}
          className="auth-form-element"
        />
        <Button
          form="reset-password-form"
          variant="outline"
          type="submit"
          disabled={blocking || disabled}
          className="[margin-top:16px] [width:100%] [min-height:48px] [margin-bottom:40px]"
        >
          Reset Password
        </Button>
      </RootFormProvider>
      <Link
        to="/login"
        className="auth-secondary-link flex f-c-c items-center justify-center"
        style={{ color: "var(--color-text--secondary)" }}
      >
        <ArrowBack className="[margin-right:8px] [font-size:16px]" />
        Back to login
      </Link>
    </div>
  );
};

export const Route = createFileRoute("/password-reset-form")({
  validateSearch: optionalSearchString("oobCode"),
  component: PasswordResetPage,
});
